import { Avatar } from "@dropbox/dig-components/avatar";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UserProfileHoverCard } from "@mirage/mosaics/BaseProfilePage/UserProfileHoverCard";
import { getInitials } from "@mirage/shared/account";
import { useIsHoverFriendly } from "@mirage/shared/responsive/hover";
import { useRef, useState } from "react";
import styles from "./ActivityLabel.module.css";
import { OverlayList } from "./OverlayList";

import type { dash_feed } from "@dropbox/api-v2-client";
import type { SyntheticEvent } from "react";

export const ActivityLabelSubject = ({
  actorEmail,
  children,
}: {
  actorEmail?: string;
  children?: React.ReactNode;
}) => {
  const text = (
    <Text variant="label" color="faint" size="small">
      {children}
    </Text>
  );
  if (actorEmail) {
    return (
      <UserProfileHoverCard
        personEmail={actorEmail}
        className={styles.activityLabelHoverCard}
      >
        {text}
      </UserProfileHoverCard>
    );
  }
  return text;
};

// Returns from 0 to 3 actorNamesToDisplay
export const getActorNames = (
  actors: dash_feed.ActivityUser[],
  numberToDisplay: number = 3,
) => {
  const actorsToDisplay = actors.filter((actor) => !!actor.name);
  let extraActorsToDisplay: dash_feed.ActivityUser[] = [];
  let actorNamesToDisplay = actorsToDisplay.map((actor) => actor.name);
  if (actorsToDisplay.length > numberToDisplay) {
    // display 2 actors + rest of the count
    actorNamesToDisplay = actorNamesToDisplay.slice(0, 2);
    extraActorsToDisplay = actorsToDisplay.slice(2);
  }

  return { actorNamesToDisplay, extraActorsToDisplay, actorsToDisplay };
};

/**
 * Use for rendering the overflow of actors in an activity label such as "3 more".
 * This component is used in conjunction with ActivityActorsLabel.
 */
export const ActivityLabelSubjectOverflow = ({
  extraActors,
  children,
}: {
  extraActors: dash_feed.ActivityUser[];
  children?: React.ReactNode;
}) => {
  const triggerRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const mouseLeaveTimeout = useRef<number | null>(null);

  const isHoverFriendly = useIsHoverFriendly();

  const handleMouseEnter = () => {
    if (mouseLeaveTimeout.current) {
      window.clearTimeout(mouseLeaveTimeout.current);
    }
    mouseLeaveTimeout.current = window.setTimeout(() => {
      setShowOverlay(true);
    }, 500);
  };

  const handleMouseLeave = () => {
    if (mouseLeaveTimeout.current) {
      window.clearTimeout(mouseLeaveTimeout.current);
    }
    mouseLeaveTimeout.current = window.setTimeout(() => {
      setShowOverlay(false);
    }, 200);
  };

  // handle touch events on mobile devices
  const handleClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    if (!isHoverFriendly) {
      handleMouseEnter();
    }
  };

  return (
    <>
      <Box
        as="span"
        ref={triggerRef}
        onPointerEnter={handleMouseEnter}
        onPointerLeave={handleMouseLeave}
        onClick={handleClick}
        style={{ cursor: "pointer", position: "relative", zIndex: 10 }} // this has a parent overlay link, so set z-index to escape that so hover events still work
      >
        <ActivityLabelSubject>{children}</ActivityLabelSubject>
      </Box>
      <OverlayList
        triggerRef={triggerRef}
        show={showOverlay}
        onPointerEnter={handleMouseEnter}
        onPointerLeave={handleMouseLeave}
      >
        {extraActors.map((user) => (
          <OverlayList.Item key={user.email}>
            <OverlayList.ItemAccessory>
              <UserProfileHoverCard personEmail={user.email}>
                <Avatar src={user.avatar_img}>
                  {getInitials(user.name || user.email || "")}
                </Avatar>
              </UserProfileHoverCard>
            </OverlayList.ItemAccessory>
            <OverlayList.ItemContent>
              <Text variant="label">
                <Truncate>{user.name || user.email}</Truncate>
              </Text>
            </OverlayList.ItemContent>
          </OverlayList.Item>
        ))}
      </OverlayList>
    </>
  );
};
